import { useState } from 'react';
import { Container, Row, Col, Alert, Card } from 'react-bootstrap';
import {
  usePushNotification,
  type PushNotificationForm as FormData,
  type SendMode,
} from '../../../apps/hooks/push-notification/usePushNotification';
import type {
  SendPushNotificationToAllResponse,
} from '../../../infrastructure/PushNotificationClient';
import { PushNotificationForm } from './components/PushNotificationForm';
import { PushNotificationPreview } from './components/PushNotificationPreview';
import { SendConfirmModal } from './components/SendConfirmModal';

const initialForm: FormData = {
  title: '',
  body: '',
  url: '',
  sendMode: 'all',
  receiverType: 'UserId',
  receiverIdentifier: '',
};

const PushNotificationPage = () => {
  const { sendNotification, isSending, error, lastResult, clearError, clearResult } = usePushNotification();
  const [form, setForm] = useState<FormData>(initialForm);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSendModeChange = (mode: SendMode) => {
    setForm((prev) => ({ ...prev, sendMode: mode }));
  };

  const handleSubmit = () => {
    setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setShowConfirm(false);
    clearResult();
    await sendNotification(form);
  };

  const isBulkResult = (result: typeof lastResult): result is SendPushNotificationToAllResponse => {
    return result !== null && 'totalDevices' in result;
  };

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <h2>Push Notification</h2>
          <p className="text-muted">Send push notifications to app users via Expo Push Service</p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={clearError}>
          {error}
        </Alert>
      )}

      {lastResult && lastResult.success && (
        <Alert variant="success" dismissible onClose={clearResult}>
          {isBulkResult(lastResult) ? (
            <>
              Push notification sent to <strong>{lastResult.totalDevices}</strong> devices.{' '}
              Success: <strong>{lastResult.successCount}</strong>, Failed: <strong>{lastResult.failureCount}</strong>
            </>
          ) : (
            <>Push notification sent successfully. Receipt ID: {lastResult.receiptId}</>
          )}
        </Alert>
      )}

      {lastResult && !lastResult.success && (
        <Alert variant="warning" dismissible onClose={clearResult}>
          {lastResult.message}
          {'error' in lastResult && lastResult.error && <>: {lastResult.error}</>}
        </Alert>
      )}

      <Row>
        <Col lg={7}>
          <Card>
            <Card.Body>
              <PushNotificationForm
                form={form}
                onChange={handleChange}
                onSendModeChange={handleSendModeChange}
                onSubmit={handleSubmit}
                isSending={isSending}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={5}>
          <Card>
            <Card.Body>
              <PushNotificationPreview title={form.title} body={form.body} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <SendConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={handleConfirmSend}
        isSending={isSending}
        sendMode={form.sendMode}
        title={form.title}
        body={form.body}
        receiverIdentifier={form.receiverIdentifier}
      />
    </Container>
  );
};

export default PushNotificationPage;
